import React, {useState} from 'react'
import {Button, Flex, Text, Box} from '../../vanilla'
import {CloseIcon} from '../icons'
import VisuallyHidden from '@reach/visually-hidden'
import {bannerContainer, bannerContent, bannerButton} from './styles.css'
import useNavigation from '../../hooks/use-navigation'
import {CopyIcon, CheckIcon} from '../icons'
import {useBannerHelper, shouldDisplayVoucherBanner} from './voucher-banner-helpers'
import { useVoucherBannerPromotionTracking, voucherAnalyticsDataVariables } from '../../analytics/promotion-tracking'
import { useSlotLocation } from '../../analytics/utils'
import {useLocation} from 'react-router-dom'
interface VoucherBannerProps {
  children?: React.ReactNode
}

type VoucherBannerActionKey = '' | 'view_offers_button' | 'terms_link' | 'copy_button'

type VoucherActionTypeData = {
  promotion_id?: string
  link_position?: number
  banner_action_status?: string
}

const VoucherBanner = ({children}: VoucherBannerProps) => {
  const {showVoucherBanner, hideVoucherBanner, formattedBannerContent} = useBannerHelper()
  const {text, dates, criteria, discountCode, enableCTA, additionalData} = formattedBannerContent
  const navigate = useNavigation()
  const [isCopied, setIsCopied] = useState(false)
  const copiedVoucherReset = React.useRef<NodeJS.Timeout | null>(null)
  const {pathname} = useLocation()
  const pageTypeValid = shouldDisplayVoucherBanner(pathname, children)
  const location = useSlotLocation()
  const [sendViewVoucherBanner, sendSelectVoucherBanner, sendDismissedVoucherBanner] = useVoucherBannerPromotionTracking()

  const voucherAnalyticsDataTemplate = (buttonAction: VoucherBannerActionKey) => {
    const actionTypeData: VoucherActionTypeData = buttonAction === '' ? {} : voucherAnalyticsDataVariables[buttonAction] || {}
    return {
      creative_name: discountCode ? 'voucher_reminder' : 'promotion_reminder',
      promotion_name: text.title,
      promotion_id: actionTypeData?.promotion_id || '',
      location_id: buttonAction || undefined,
      campaign_ID: additionalData?.promotion_id,
      link_position: actionTypeData?.link_position || 1,
      banner_action_status: actionTypeData?.banner_action_status || '',
      creative_slot: `${location}_voucher-banner_${text.title}`.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
    }
  }

  const handleCopyClick = () => {
    setIsCopied(true)
    if (copiedVoucherReset.current) {
      clearTimeout(copiedVoucherReset.current)
    }
    copiedVoucherReset.current = setTimeout(() => {
      setIsCopied(false)
    }, 2000)
  }

  const copyCode = async (code: string) => {
    if (typeof navigator === 'undefined') return false
    if (!navigator.clipboard.writeText) return false

    await navigator.clipboard.writeText(code)
    return true
  }

  if (!pageTypeValid || !showVoucherBanner) {
    return null
  }

  setTimeout(() => {
    sendViewVoucherBanner(voucherAnalyticsDataTemplate(''))
  }, 0)

  return (
    <Flex className={bannerContainer} data-test-selector="voucher-banner">
      <Flex
        width="full"
        justify="space-between"
        align="center"
        flexWrap={['wrap', 'nowrap']}
        gap="16px"
        style={{maxWidth: '1200px', margin: '0 auto'}}
        flexDirection="row"
      >
        <Flex className={bannerContent}>
          <Text variant="heading1" color="black" data-test-selector="voucher-banner-title">
            {text.title}
          </Text>
          <Text variant="text3" color="black" data-test-selector="voucher-banner-description">
            {text.description}{' '}
            {criteria?.minSpend ? `Minimum spend of £${criteria.minSpend}. ` : ''}
            {criteria?.maximumPerOrder ? `Maximum of ${criteria.maximumPerOrder} per order. ` : ''}
            {dates?.endDate
              ? `Offer valid for delivery ${
                  dates?.startDate ? `from ${dates.startDate} ` : ''
                }until ${dates.endDate}. `
              : ' Offer subject to availability. '}
            Exclusions apply.{' '}
            <a href="/terms.html#discounts-coupons" onClick={() => sendSelectVoucherBanner(voucherAnalyticsDataTemplate('terms_link'))} style={{ textDecoration: 'underline' }}>T&Cs apply.</a>
          </Text>
        </Flex>
        {enableCTA && (
          <Flex align="center" gap="24px">
            <Flex className={bannerButton}>
              {discountCode && (
                <Box data-test-selector="voucher-banner-discount-code" paddingX="12px">
                  <Text variant="text3" color="black">
                    {discountCode}
                  </Text>
                </Box>
              )}
              <Button
                variant="primary"
                iconLeft={
                  discountCode ? (
                    isCopied ? (
                      <CheckIcon boxSize="16px" />
                    ) : (
                      <CopyIcon boxSize="16px" />
                    )
                  ) : undefined
                }
                onClick={async () => {
                  sendSelectVoucherBanner(voucherAnalyticsDataTemplate(discountCode ? 'copy_button' : 'view_offers_button'))
                  if (discountCode) {
                    const copied = await copyCode(discountCode)
                    if (copied) handleCopyClick()
                    return
                  }
                  navigate('/offers')
                }}
                zIndex={'1'}
                data-test-selector="voucher-banner-cta-button"
              >
                {discountCode ? (isCopied ? 'Copied' : 'Copy') : 'View Offers'}
              </Button>
            </Flex>
          </Flex>
        )}
      </Flex>
      <Button
        variant="unstyled"
        width="44px"
        onClick={() => {
          sendDismissedVoucherBanner(voucherAnalyticsDataTemplate(''))
          hideVoucherBanner()
        }}
        data-test-selector="voucher-banner-close-button"
      >
        <VisuallyHidden>Close</VisuallyHidden>
        <CloseIcon boxSize="24px" />
      </Button>
    </Flex>
  )
}

export default VoucherBanner
